import React, { useEffect, useMemo, useState } from 'react';
import {
  listSupportTickets,
  updateSupportTicket,
} from '../../../services/supportApi';

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'open', label: 'Abiertos' },
  { value: 'in_progress', label: 'En progreso' },
  { value: 'resolved', label: 'Resueltos' },
];

const TYPE_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'support', label: 'Consulta' },
  { value: 'bug', label: 'Error' },
];

const PRIORITY_OPTIONS = [
  { value: 'all', label: 'Todas' },
  { value: 'high', label: 'Alta' },
  { value: 'medium', label: 'Media' },
  { value: 'low', label: 'Baja' },
];

const STATUS_LABELS = {
  open: 'Abierto',
  in_progress: 'En progreso',
  resolved: 'Resuelto',
};

const TYPE_LABELS = {
  support: 'Consulta',
  bug: 'Error',
};

const PRIORITY_LABELS = {
  high: 'Alta',
  medium: 'Media',
  low: 'Baja',
};

const formatDateTime = value => {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  } catch (error) {
    return value;
  }
};

const resolveErrorMessage = error => {
  if (!error) return 'Ocurrio un error inesperado.';
  if (typeof error === 'string') return error;
  return (
    error?.data?.message ||
    error?.message ||
    error?.response?.data?.message ||
    'Ocurrio un error inesperado.'
  );
};

export default function SupportTab({ currentUser, companyOptions = [] }) {
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    status: 'open',
    type: 'all',
    priority: 'all',
    companyId: 'all',
    search: '',
  });
  const [selectedId, setSelectedId] = useState('');
  const [statusDraft, setStatusDraft] = useState('open');
  const [notesDraft, setNotesDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [feedbackTone, setFeedbackTone] = useState('success');

  const refreshTickets = async () => {
    setLoading(true);
    setError('');
    try {
      const { items } = await listSupportTickets({
        status: 'all',
        perPage: 500,
        sort: '-created',
      });
      setTickets(items);
    } catch (err) {
      setError(resolveErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshTickets();
  }, []);

  useEffect(() => {
    if (!tickets.length) {
      setSelectedId('');
      return;
    }
    const exists = tickets.some(ticket => ticket.id === selectedId);
    if (!exists) {
      setSelectedId(tickets[0].id);
    }
  }, [tickets, selectedId]);

  const selectedTicket = useMemo(
    () => tickets.find(ticket => ticket.id === selectedId) || null,
    [tickets, selectedId]
  );

  useEffect(() => {
    if (!selectedTicket) {
      setStatusDraft('open');
      setNotesDraft('');
      return;
    }
    setStatusDraft(selectedTicket.status || 'open');
    setNotesDraft(selectedTicket.resolutionNotes || '');
  }, [selectedTicket]);

  const stats = useMemo(() => {
    return tickets.reduce(
      (acc, ticket) => {
        if (ticket.status === 'open') acc.open += 1;
        else if (ticket.status === 'in_progress') acc.progress += 1;
        else if (ticket.status === 'resolved') acc.resolved += 1;
        return acc;
      },
      { open: 0, progress: 0, resolved: 0 }
    );
  }, [tickets]);

  const filteredTickets = useMemo(() => {
    const searchTerm = filters.search.trim().toLowerCase();

    return tickets
      .filter(ticket => {
        if (filters.status !== 'all' && ticket.status !== filters.status) {
          return false;
        }
        if (filters.type !== 'all' && ticket.ticketType !== filters.type) {
          return false;
        }
        if (filters.priority !== 'all') {
          const ticketPriority = ticket.priority || 'medium';
          if (ticketPriority !== filters.priority) {
            return false;
          }
        }
        if (filters.companyId !== 'all' && ticket.companyId !== filters.companyId) {
          return false;
        }
        if (searchTerm) {
          const haystack = [
            ticket.subject,
            ticket.description,
            ticket.reporter?.name,
            ticket.reporter?.email,
            ticket.company?.name,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          if (!haystack.includes(searchTerm)) {
            return false;
          }
        }
        return true;
      })
      .sort((a, b) => {
        const aDate = a.updated || a.created;
        const bDate = b.updated || b.created;
        return new Date(bDate || 0) - new Date(aDate || 0);
      });
  }, [tickets, filters]);

  useEffect(() => {
    if (!filteredTickets.length) {
      return;
    }
    const exists = filteredTickets.some(ticket => ticket.id === selectedId);
    if (!exists) {
      setSelectedId(filteredTickets[0].id);
    }
  }, [filteredTickets, selectedId]);

  const handleFilterChange = event => {
    const { name, value } = event.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearchChange = event => {
    const { value } = event.target;
    setFilters(prev => ({
      ...prev,
      search: value,
    }));
  };

  const handleClearSearch = () => {
    setFilters(prev => ({
      ...prev,
      search: '',
    }));
  };

  const handleSelectTicket = id => {
    setSelectedId(id);
    setFeedback('');
  };

  const persistTicket = async nextStatus => {
    if (!selectedTicket) return;

    const statusToSave = nextStatus || statusDraft;

    setSaving(true);
    setFeedback('');

    try {
      const payload = {
        status: statusToSave,
        resolutionNotes: notesDraft,
      };

      if (statusToSave === 'resolved') {
        payload.resolvedById = currentUser?.id || '';
        payload.resolvedAt = new Date().toISOString();
      } else if (statusToSave === 'in_progress') {
        payload.resolvedById = currentUser?.id || '';
        payload.resolvedAt = '';
      } else {
        payload.resolvedById = '';
        payload.resolvedAt = '';
      }

      const updated = await updateSupportTicket(selectedTicket.id, payload);

      setTickets(prev =>
        prev.map(ticket => (ticket.id === updated.id ? updated : ticket))
      );
      setStatusDraft(updated.status || statusToSave);
      setNotesDraft(updated.resolutionNotes || notesDraft);
      setFeedbackTone('success');
      setFeedback('Ticket actualizado correctamente.');
    } catch (err) {
      setFeedbackTone('error');
      setFeedback(resolveErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = event => {
    event?.preventDefault?.();
    persistTicket();
  };

  const handleQuickStatus = status => {
    setStatusDraft(status);
    persistTicket(status);
  };

  return (
    <section className="admin-panel">
      <div className="admin-panel-card wide admin-support-card">
        <div className="admin-panel-head">
          <div>
            <h2>Tickets de soporte</h2>
            <p>Gestiona los reportes enviados por las empresas y usuarios.</p>
          </div>
          <button
            type="button"
            className="admin-subtab"
            onClick={refreshTickets}
            disabled={loading}
          >
            {loading ? 'Actualizando...' : 'Actualizar'}
          </button>
        </div>

        <div className="admin-support-stats">
          <div className="admin-support-stat stat-open">
            <strong>{stats.open}</strong>
            <span>Abiertos</span>
          </div>
          <div className="admin-support-stat stat-progress">
            <strong>{stats.progress}</strong>
            <span>En progreso</span>
          </div>
          <div className="admin-support-stat stat-resolved">
            <strong>{stats.resolved}</strong>
            <span>Resueltos</span>
          </div>
        </div>

        <div className="admin-support-filters">
          <label className="admin-support-field admin-support-search">
            <span>Buscar</span>
            <div className="admin-support-searchbox">
              <input
                type="search"
                value={filters.search}
                onChange={handleSearchChange}
                placeholder="Asunto, descripcion, usuario o empresa"
              />
              {filters.search ? (
                <button type="button" onClick={handleClearSearch} aria-label="Limpiar busqueda">
                  Limpiar
                </button>
              ) : null}
            </div>
          </label>

          <label className="admin-support-field">
            <span>Estado</span>
            <select
              className="admin-select"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
            >
              {STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="admin-support-field">
            <span>Tipo</span>
            <select
              className="admin-select"
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
            >
              {TYPE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="admin-support-field">
            <span>Prioridad</span>
            <select
              className="admin-select"
              name="priority"
              value={filters.priority}
              onChange={handleFilterChange}
            >
              {PRIORITY_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="admin-support-field">
            <span>Empresa</span>
            <select
              className="admin-select"
              name="companyId"
              value={filters.companyId}
              onChange={handleFilterChange}
            >
              <option value="all">Todas</option>
              {companyOptions.map(company => (
                <option key={company.id} value={company.id}>
                  {company.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error ? <div className="admin-alert error">{error}</div> : null}

        <div className="admin-support-table">
          {loading ? (
            <div className="admin-support-empty">Cargando tickets...</div>
          ) : filteredTickets.length ? (
            <div className="admin-scroll">
              <table className="admin-table compact">
                <thead>
                  <tr>
                    <th>Folio</th>
                    <th>Asunto</th>
                    <th>Estado</th>
                    <th>Tipo</th>
                    <th>Prioridad</th>
                    <th>Empresa</th>
                    <th>Ultima actualizacion</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTickets.map(ticket => (
                    <tr
                      key={ticket.id}
                      className={ticket.id === selectedId ? 'is-active' : ''}
                      onClick={() => handleSelectTicket(ticket.id)}
                    >
                      <td>#{ticket.id}</td>
                      <td>{ticket.subject}</td>
                      <td>
                        <span className={`admin-support-badge status-${ticket.status}`}>
                          {STATUS_LABELS[ticket.status] || ticket.status}
                        </span>
                      </td>
                      <td>{TYPE_LABELS[ticket.ticketType] || ticket.ticketType}</td>
                      <td>{PRIORITY_LABELS[ticket.priority] || ticket.priority || 'Media'}</td>
                      <td>{ticket.company?.name || 'Sin asignar'}</td>
                      <td>{formatDateTime(ticket.updated || ticket.created)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="admin-support-empty">
              No se encontraron tickets con los filtros seleccionados.
            </div>
          )}
        </div>
      </div>

      <div className="admin-panel-card admin-support-detail">
        {selectedTicket ? (
          <>
            <header>
              <h3>{selectedTicket.subject}</h3>
              <span className={`admin-support-badge status-${selectedTicket.status}`}>
                {STATUS_LABELS[selectedTicket.status] || selectedTicket.status}
              </span>
            </header>

            <div className="admin-support-meta">
              <dl>
                <div>
                  <dt>Reportado por</dt>
                  <dd>
                    {selectedTicket.reporter?.name || 'Sin nombre'}
                    {selectedTicket.reporter?.email ? (
                      <span>{selectedTicket.reporter.email}</span>
                    ) : null}
                  </dd>
                </div>
                <div>
                  <dt>Empresa</dt>
                  <dd>{selectedTicket.company?.name || 'No asociada'}</dd>
                </div>
                <div>
                  <dt>Tipo</dt>
                  <dd>{TYPE_LABELS[selectedTicket.ticketType] || selectedTicket.ticketType}</dd>
                </div>
                <div>
                  <dt>Prioridad</dt>
                  <dd>{PRIORITY_LABELS[selectedTicket.priority] || 'Media'}</dd>
                </div>
                <div>
                  <dt>Creado</dt>
                  <dd>{formatDateTime(selectedTicket.created)}</dd>
                </div>
                <div>
                  <dt>Actualizado</dt>
                  <dd>{formatDateTime(selectedTicket.updated)}</dd>
                </div>
              </dl>
            </div>

            <section className="admin-support-description">
              <h4>Descripcion</h4>
              <p>{selectedTicket.description}</p>
            </section>

            <form className="admin-support-form" onSubmit={handleSave}>
              <label>
                <span>Estado del ticket</span>
                <select
                  className="admin-select"
                  value={statusDraft}
                  onChange={event => setStatusDraft(event.target.value)}
                >
                  {STATUS_OPTIONS.filter(option => option.value !== 'all').map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span>Notas de resolucion</span>
                <textarea
                  value={notesDraft}
                  onChange={event => setNotesDraft(event.target.value)}
                  rows={4}
                  placeholder="Agrega seguimiento o acciones realizadas."
                />
              </label>

              {feedback ? (
                <div className={`admin-alert ${feedbackTone === 'error' ? 'error' : ''}`}>
                  {feedback}
                </div>
              ) : null}

              <div className="admin-support-actions">
                <button
                  type="button"
                  className="admin-subtab"
                  onClick={() => handleQuickStatus('open')}
                  disabled={saving || selectedTicket.status === 'open'}
                >
                  Reabrir
                </button>
                <button
                  type="button"
                  className="admin-subtab"
                  onClick={() => handleQuickStatus('in_progress')}
                  disabled={saving || selectedTicket.status === 'in_progress'}
                >
                  Marcar en progreso
                </button>
                <button
                  type="button"
                  className="admin-subtab"
                  onClick={() => handleQuickStatus('resolved')}
                  disabled={saving || selectedTicket.status === 'resolved'}
                >
                  Marcar resuelto
                </button>
                <button type="submit" className="admin-primary" disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="admin-support-empty">
            Selecciona un ticket para revisar los detalles.
          </div>
        )}
      </div>
    </section>
  );
}
